import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EXIT_INTENT_COOLDOWN_MS, EXIT_INTENT_EXCLUDED_PATHS, EXIT_INTENT_MAX_SHOWINGS } from '@config/exit-intent.constants';
import { ApplicationStateService } from '@services/application-state.service';
import { filter, fromEvent, merge } from 'rxjs';

const SHOWN_COUNT_KEY = 'isi-dtc-exit-intent-shown-count';
const LAST_SHOWN_AT_KEY = 'isi-dtc-exit-intent-last-shown-at';

@Injectable({ providedIn: 'root' })
export class ExitIntentService {
    private readonly document = inject(DOCUMENT);
    private readonly destroyRef = inject(DestroyRef);
    private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly router = inject(Router);

    readonly dialogVisible = signal(false);
    readonly maxShowings = signal(EXIT_INTENT_MAX_SHOWINGS);
    private readonly shownCount = signal(this.readPersistedNumber(SHOWN_COUNT_KEY));
    private lastShownAt = this.readPersistedNumber(LAST_SHOWN_AT_KEY);

    constructor() {
        if (!this.isBrowser) return;

        const desktopExit$ = fromEvent<MouseEvent>(this.document, 'mouseout', { passive: true }).pipe(
            filter((event) => this.isCursorExit(event)),
        );

        const mobileExit$ = fromEvent(this.document, 'visibilitychange').pipe(filter(() => this.document.hidden));

        merge(desktopExit$, mobileExit$)
            .pipe(
                filter(() => this.canShow()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.show());
    }

    dismiss(): void {
        this.dialogVisible.set(false);
    }

    private canShow(): boolean {
        if (this.dialogVisible()) return false;
        if (this.applicationStateService.isCompleted()) return false;
        if (this.isExcludedRoute()) return false;
        if (this.shownCount() >= this.maxShowings()) return false;
        if (Date.now() - this.lastShownAt < EXIT_INTENT_COOLDOWN_MS) return false;
        return true;
    }

    private isExcludedRoute(): boolean {
        const path = this.router.url.split('?')[0].split('#')[0];
        return EXIT_INTENT_EXCLUDED_PATHS.some((excluded) => path === excluded || path.startsWith(`${excluded}/`));
    }

    private show(): void {
        this.dialogVisible.set(true);
        this.shownCount.update((count) => count + 1);
        this.lastShownAt = Date.now();
        this.persistNumber(SHOWN_COUNT_KEY, this.shownCount());
        this.persistNumber(LAST_SHOWN_AT_KEY, this.lastShownAt);
    }

    private isCursorExit(event: MouseEvent): boolean {
        // Triggered when the cursor leaves the document through the top edge of the viewport,
        // and the mouse is not moving onto another element within the document.
        return event.clientY <= 0 && !event.relatedTarget;
    }

    private readPersistedNumber(key: string): number {
        if (typeof sessionStorage === 'undefined') return 0;
        const raw = sessionStorage.getItem(key);
        const parsed = raw ? Number.parseInt(raw, 10) : 0;
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    }

    private persistNumber(key: string, value: number): void {
        if (typeof sessionStorage === 'undefined') return;
        sessionStorage.setItem(key, String(value));
    }
}
